/**
 * OAuth Authentication Service
 * 
 * Handles Google OAuth authentication for multi-tenant system
 * Uses centralized OAuth endpoint for all tenants
 */

import axiosInstance from '../config/axios.config';
import axios from 'axios';

// Create axios instance for central OAuth API
const centralAPI = axios.create({
    baseURL: `${process.env.REACT_APP_CENTRAL_URL || 'http://localhost:8000/api/central'}`,
    headers: {
        'Content-Type': 'application/json',
    }
});

class OAuthService {
    /**
     * Get Frontend Host URL
     * Constructs the complete frontend URL for OAuth callbacks
     */
    getFrontendHost() {
        const host = window.location.hostname;
        const port = window.location.port ? `:${window.location.port}` : '';
        const protocol = window.location.protocol;
        return `${protocol}//${host}${port}`;
    }

    /**
     * Get Domain Information for OAuth
     * Gets domain_id which is needed for OAuth without exposing tenant_id
     */
    async getDomainInfo() {
        try {
            const response = await axiosInstance.get('/auth/domain-info');
            
            if (response.data.success) {
                return {
                    success: true,
                    data: response.data.data
                };
            }

            return {
                success: false,
                message: 'Failed to get domain information'
            };
        } catch (error) {
            console.error('Domain info error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to identify domain'
            };
        }
    }

    /**
     * Initiate Google OAuth Flow
     * Gets the Google OAuth URL and redirects user
     */
    async initiateGoogleOAuth(companyToken = null, advisorToken = null) {
        try {
            // First get domain info
            const domainResult = await this.getDomainInfo();

            if (!domainResult.success) {
                return {
                    success: false,
                    message: 'Failed to identify tenant domain'
                };
            }

            const { domain_id, domain } = domainResult.data;

            // Store domain info for later use
            sessionStorage.setItem('oauth_domain_id', domain_id);
            sessionStorage.setItem('oauth_domain', domain);

            // Store company token if present (for validation after callback)
            if (companyToken) {
                sessionStorage.setItem('oauth_company_token', companyToken);
            }

            // Store advisor token if present (for MLM referral after callback)
            if (advisorToken) {
                sessionStorage.setItem('oauth_advisor_token', advisorToken);
            }

            // Get frontend host URL using our helper function
            const frontendHost = this.getFrontendHost();

            // Get OAuth URL from central server
            const response = await centralAPI.post('/tenant-oauth/google/url', {
                domain_id: domain_id,
                domain: window.location.hostname,
                host: frontendHost,
                return_url: '/app/dashboard',
                company_token: companyToken, // Pass company token to backend
                advisor_token: advisorToken  // Pass MLM advisor token to backend
            });

            if (response.data.success) {
                // Store state for validation
                sessionStorage.setItem('oauth_state', response.data.state);

                return {
                    success: true,
                    oauth_url: response.data.oauth_url,
                    state: response.data.state
                };
            }

            return {
                success: false,
                message: response.data.message || 'Failed to generate OAuth URL'
            };
        } catch (error) {
            console.error('OAuth URL error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to initiate Google authentication'
            };
        }
    }

    /**
     * Handle OAuth Callback
     * Processes the response from Google OAuth
     */
    async handleOAuthCallback(code, state, error = null) {
        try {
            // Check if user cancelled
            if (error) {
                return {
                    success: false,
                    message: error === 'access_denied' ? 'Authentication cancelled' : 'Authentication failed',
                    error: error
                };
            }

            // Validate state to prevent CSRF
            const storedState = sessionStorage.getItem('oauth_state');
            if (state !== storedState) {
                console.error('State mismatch - possible CSRF attack');
                return {
                    success: false,
                    message: 'Security validation failed. Please try again.'
                };
            }

            // Clear stored OAuth data
            sessionStorage.removeItem('oauth_state');
            sessionStorage.removeItem('oauth_domain_id');
            sessionStorage.removeItem('oauth_domain');
            sessionStorage.removeItem('oauth_company_token');
            sessionStorage.removeItem('oauth_advisor_token');

            // Exchange code for token via central server
            const response = await centralAPI.post('/tenant-oauth/google/callback', null, {
                params: { code, state }
            });

            if (response.data.success) {
                const { token, user, new_user, tenant } = response.data.data;
                
                // Store auth data
                this.setAuthData(token, user);
                
                return {
                    success: true,
                    user: user,
                    new_user: new_user,
                    tenant: tenant,
                    message: new_user ? 'Welcome! Your account has been created.' : 'Welcome back!'
                };
            }

            return {
                success: false,
                message: response.data.message || 'Authentication failed'
            };
        } catch (error) {
            console.error('OAuth callback error:', error);
            
            // Clear any partial OAuth data
            sessionStorage.removeItem('oauth_state');
            sessionStorage.removeItem('oauth_domain_id');
            sessionStorage.removeItem('oauth_domain');
            sessionStorage.removeItem('oauth_company_token');
            sessionStorage.removeItem('oauth_advisor_token');

            return {
                success: false,
                message: error.response?.data?.message || 'OAuth authentication failed. Please try again.'
            };
        }
    }

    /**
     * Alternative: Exchange OAuth code for token
     * Used if React handles the redirect itself
     */
    async exchangeCodeForToken(code, state) {
        try {
            const response = await centralAPI.post('/tenant-oauth/google/exchange', {
                code: code,
                state: state
            });

            if (response.data.success) {
                const { token, user, new_user, tenant } = response.data.data;
                
                // Store auth data
                this.setAuthData(token, user);
                
                return {
                    success: true,
                    user: user,
                    new_user: new_user,
                    tenant: tenant
                };
            }

            return {
                success: false,
                message: response.data.message || 'Failed to exchange code'
            };
        } catch (error) {
            console.error('Code exchange error:', error);
            return {
                success: false,
                message: error.response?.data?.message || 'Failed to complete authentication'
            };
        }
    }

    /**
     * Complete OAuth registration for brand-new users.
     * Called by the "Complete your signup" modal after the user provides
     * their mobile number. The pending token + mobile are sent to the
     * central endpoint; on success we receive the login token + user data.
     */
    async completeRegistration(pendingToken, mobileNumber) {
        try {
            const response = await centralAPI.post('/tenant-oauth/google/complete-registration', {
                pending_token: pendingToken,
                mobile_number: mobileNumber,
            });

            if (response.data?.success) {
                return {
                    success: true,
                    data: response.data.data,
                };
            }

            return {
                success: false,
                message: response.data?.message || 'Registration failed. Please try again.',
            };
        } catch (error) {
            console.error('Complete registration error:', error);

            // Surface field-level validation errors if present
            const fieldErrors = error.response?.data?.errors;
            if (fieldErrors?.mobile_number?.length) {
                return {
                    success: false,
                    message: fieldErrors.mobile_number[0],
                    status: error.response?.status,
                };
            }

            return {
                success: false,
                message: error.response?.data?.message || 'Something went wrong. Please try again.',
                status: error.response?.status,
            };
        }
    }

    /**
     * Check if currently processing OAuth
     */
    isOAuthInProgress() {
        return !!sessionStorage.getItem('oauth_state');
    }

    /**
     * Cancel OAuth process
     */
    cancelOAuth() {
        sessionStorage.removeItem('oauth_state');
        sessionStorage.removeItem('oauth_domain_id');
        sessionStorage.removeItem('oauth_domain');
        sessionStorage.removeItem('oauth_company_token');
        sessionStorage.removeItem('oauth_advisor_token');
    }

    /**
     * Set auth data in localStorage
     */
    setAuthData(token, user) {
        if (token) {
            localStorage.setItem('auth_token', token);
        }
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
        }
    }

    /**
     * Clear auth data
     */
    clearAuthData() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        this.cancelOAuth();
    }

    /**
     * Get stored user
     */
    getStoredUser() {
        const userStr = localStorage.getItem('user');
        if (userStr) {
            try {
                return JSON.parse(userStr);
            } catch (e) {
                return null;
            }
        }
        return null;
    }

    /**
     * Check if user is authenticated
     */
    isAuthenticated() {
        const token = localStorage.getItem('auth_token');
        const user = localStorage.getItem('user');
        return !!(token && user);
    }
}

export default new OAuthService();